/**
 * Builders for billing/payroll line items created from expenses.
 *
 * Billing and payroll items share the same meta_data shape; payroll items
 * additionally carry a `status: "PENDING"` marker.
 */

export interface Worker {
  id: string;
  first_name: string;
  last_name: string;
}

export interface ExpenseLineItemInput {
  amount?: number;
  description?: string;
  quantity?: number;
  unit?: string;
  shift_id?: string;
  user_id?: string;
  project_id?: string;
  expense_id?: string;
}

/**
 * Formats a numeric amount as a `"$0.00"` rate label, or null when there is no
 * amount. Kept null-returning (not "$0.00") so callers can distinguish
 * "no rate" from "zero rate".
 *
 * @example
 * formatRateLabel(50); // "$50.00"
 * formatRateLabel(0); // null
 * formatRateLabel(undefined); // null
 */
export function formatRateLabel(amount?: number | null): string | null {
  return amount ? `$${Number(amount).toFixed(2)}` : null;
}

/**
 * Builds the `meta_data` payload for a billing or payroll item created from an
 * expense. Pass `{ pending: true }` for payroll items to include the
 * `status: "PENDING"` marker.
 *
 * @example
 * buildExpenseItemMetaData({ amount: 50, description: "Van" }, {});
 * // { rate: "$50.00", description: "Van", quantity: 1, unit: "-", ... }
 */
export function buildExpenseItemMetaData(
  item: ExpenseLineItemInput,
  userMap: Record<string, Worker>,
  opts: { pending?: boolean } = {},
): Record<string, unknown> {
  const meta: Record<string, unknown> = {
    rate: formatRateLabel(item.amount),
    description: item.description,
    quantity: item.quantity || 1,
    unit: item.unit || "-",
    shift_id: item.shift_id || null,
    worker: item.user_id ? userMap[item.user_id] || null : null,
    project_id: item.project_id || null,
    links: {
      expenses: [item.expense_id],
    },
  };
  if (opts.pending) meta.status = "PENDING";
  return meta;
}
